import DownloadIcon from '@mui/icons-material/GetApp';
import { useState } from 'react';
import { Confirm, useListContext, useResourceContext } from 'react-admin';

import { BOResourceAll } from '@Common/types/bo_external/resources';

import { Button } from '@sh4res/bo-common/components/Button';
import { ServerExportBody, useServerExport } from '@sh4res/bo-common/hooks/useServerExport';
import { isBoExternal } from '@sh4res/bo-common/utils/env';

type ServerExportButtonProps = {
  /** The list the export is under, whose `list` permission it requires */
  resource: BOResourceAll;
  /** Read on click, so that it can depend on the current filters */
  getBody: () => ServerExportBody;
  label?: string;
  disabled?: boolean;
  /** Asks to confirm before exporting. See `useExportNeedsConfirmation` */
  needsConfirmation?: boolean;
  'data-testid'?: string;
};

const isFilterValueSet = (value: unknown) =>
  value !== undefined && value !== null && value !== '' && !(Array.isArray(value) && !value.length);

/**
 * Whether exporting the list should be confirmed: in the internal BO, when no filter is applied
 * (as exporting the whole list might take a lot of time). To be used inside a list.
 */
export const useExportNeedsConfirmation = () => {
  const { filterValues } = useListContext();

  return !isBoExternal() && !Object.values(filterValues ?? {}).some(isFilterValueSet);
};

/** Downloads a file the backend builds. See `useServerExport`. */
export const ServerExportButton = ({
  resource,
  getBody,
  label = 'ra.action.export',
  disabled,
  needsConfirmation,
  'data-testid': dataTestId,
}: ServerExportButtonProps) => {
  const { exportFile, isExporting } = useServerExport();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const handleConfirm = async () => {
    setIsConfirmOpen(false);
    await exportFile(resource, getBody());
  };

  return (
    <>
      <Button
        label={label}
        variant="text"
        size="small"
        disabled={disabled || isExporting}
        onClick={async () =>
          needsConfirmation ? setIsConfirmOpen(true) : exportFile(resource, getBody())
        }
        permissions={{ resource, action: 'list' }}
        data-testid={dataTestId}>
        <DownloadIcon />
      </Button>
      {needsConfirmation && (
        <Confirm
          isOpen={isConfirmOpen}
          title="resources.bo_common.export.without_filter.title"
          content="resources.bo_common.export.without_filter.content"
          onConfirm={handleConfirm}
          onClose={() => setIsConfirmOpen(false)}
        />
      )}
    </>
  );
};

/**
 * The list's rows, as its current filters and sort select them, in a CSV: what react-admin's
 * `ExportButton` did in the browser, for any number of rows.
 */
export const ListCsvExportButton = ({ 'data-testid': dataTestId }: { 'data-testid'?: string }) => {
  const resource = useResourceContext() as BOResourceAll;
  // `filter` is the list's fixed filter, which react-admin keeps apart from the user's
  const { filter, filterValues, sort, total } = useListContext();
  const needsConfirmation = useExportNeedsConfirmation();

  return (
    <ServerExportButton
      resource={resource}
      disabled={total === 0}
      needsConfirmation={needsConfirmation}
      getBody={() => ({
        kind: 'list_csv',
        query: {
          filter: JSON.stringify({ ...filterValues, ...filter }),
          sort: JSON.stringify([sort.field, sort.order]),
        },
      })}
      data-testid={dataTestId}
    />
  );
};
